/**
 * Pure TEA update for writing-surface display mode (Focus / Reading / Writing).
 *
 * Shell: `useDisplayModeStore.ts` (Pinia + sync session panel Cmds).
 * Eligibility (Notes/map/storyboard/embed) is checked in the shell before
 * EnterFocus / EnterReading; this update trusts those guards.
 */
import { assertNever } from "../lib/assertNever";
import { displayModeIsReading, type DisplayMode } from "../lib/displayMode";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type PanelSnapshot = {
  readonly outlineOpen: boolean;
  readonly commentsOpen: boolean;
  readonly historyOpen: boolean;
  readonly goalsOpen: boolean;
};

export type DisplayModeModel = {
  readonly displayMode: DisplayMode;
  readonly modeLocked: boolean;
  readonly typewriterPref: boolean;
  readonly savedPanels: Maybe<PanelSnapshot>;
};

export function initialDisplayModeModel(
  typewriterPref: boolean,
): DisplayModeModel {
  return {
    displayMode: "editor",
    modeLocked: false,
    typewriterPref,
    savedPanels: nothing,
  };
}

export type DisplayModeMsg =
  | { readonly type: "LockMode"; readonly locked: boolean }
  | { readonly type: "SetDisplayMode"; readonly mode: DisplayMode }
  | {
      readonly type: "EnterFocus";
      readonly snapshot: PanelSnapshot;
    }
  | { readonly type: "ExitFocus" }
  | { readonly type: "EnterReading" }
  | { readonly type: "ExitReading" }
  | { readonly type: "ToggleTypewriterPref" }
  /** modeEligible flipped false while unlocked — drop Focus / Reading. */
  | { readonly type: "EligibilityLost" };

export type DisplayModeCmd =
  | { readonly kind: "restorePanels"; readonly snapshot: PanelSnapshot }
  | { readonly kind: "hideFocusPanels" }
  | { readonly kind: "hideReadingSidePanels" }
  | { readonly kind: "persistTypewriterPref"; readonly value: boolean };

function restoreCmds(saved: Maybe<PanelSnapshot>): readonly DisplayModeCmd[] {
  if (isNothing(saved)) return [];
  return [{ kind: "restorePanels", snapshot: saved.value }];
}

function leaveFocus(
  model: DisplayModeModel,
): readonly [DisplayModeModel, readonly DisplayModeCmd[]] {
  if (model.displayMode !== "focus") {
    return [model, []];
  }
  return [{ ...model, savedPanels: nothing }, restoreCmds(model.savedPanels)];
}

export function updateDisplayMode(
  model: DisplayModeModel,
  msg: DisplayModeMsg,
): readonly [DisplayModeModel, readonly DisplayModeCmd[]] {
  switch (msg.type) {
    case "LockMode":
      return [{ ...model, modeLocked: msg.locked }, []];
    case "SetDisplayMode": {
      if (msg.mode === model.displayMode) return [model, []];
      if (msg.mode === "focus") {
        return [{ ...model, displayMode: msg.mode }, []];
      }
      const [afterLeave, leaveCmds] = leaveFocus(model);
      return [{ ...afterLeave, displayMode: msg.mode }, leaveCmds];
    }
    case "EnterFocus": {
      if (model.displayMode === "focus") return [model, []];
      const [afterLeave, leaveCmds] = leaveFocus(model);
      return [
        {
          ...afterLeave,
          displayMode: "focus",
          savedPanels: just(msg.snapshot),
        },
        [...leaveCmds, { kind: "hideFocusPanels" }],
      ];
    }
    case "ExitFocus": {
      if (model.displayMode !== "focus") return [model, []];
      const [afterLeave, leaveCmds] = leaveFocus(model);
      return [{ ...afterLeave, displayMode: "editor" }, leaveCmds];
    }
    case "EnterReading": {
      if (displayModeIsReading(model.displayMode)) return [model, []];
      const [afterLeave, leaveCmds] = leaveFocus(model);
      return [
        { ...afterLeave, displayMode: "readingComments" },
        [...leaveCmds, { kind: "hideReadingSidePanels" }],
      ];
    }
    case "ExitReading": {
      if (
        displayModeIsReading(model.displayMode) === false ||
        model.modeLocked
      ) {
        return [model, []];
      }
      return [{ ...model, displayMode: "editor" }, []];
    }
    case "ToggleTypewriterPref": {
      const value = model.typewriterPref === false;
      return [
        { ...model, typewriterPref: value },
        [{ kind: "persistTypewriterPref", value }],
      ];
    }
    case "EligibilityLost": {
      if (model.modeLocked) return [model, []];
      if (model.displayMode === "focus") {
        const [afterLeave, leaveCmds] = leaveFocus(model);
        return [{ ...afterLeave, displayMode: "editor" }, leaveCmds];
      }
      if (displayModeIsReading(model.displayMode)) {
        return [{ ...model, displayMode: "editor" }, []];
      }
      return [model, []];
    }
    default:
      return assertNever(msg);
  }
}

export function isFocusMode(model: DisplayModeModel): boolean {
  return model.displayMode === "focus";
}

export function savedPanelsSnapshot(
  model: DisplayModeModel,
): Maybe<PanelSnapshot> {
  return isJust(model.savedPanels) ? model.savedPanels : nothing;
}

export const updateDisplayModeFn: UpdateFn<
  DisplayModeModel,
  DisplayModeMsg,
  DisplayModeCmd
> = updateDisplayMode;
